import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type { ImportTextResponse } from '@mlc/shared';
import { importFromText, readAiAvailability } from './api';
import { Banner, Button, Card, Icon, fieldClasses } from './ui';

// Style de champ partagé (B1, refonte UX/UI passe 3) : voir ui/Field.tsx. Le
// texte collé reste en `font-mono` (lisibilité d'un bloc de texte brut,
// inchangé depuis l'origine), ajouté par-dessus le style de champ commun.
const textareaClass = `${fieldClasses('default')} min-h-48 font-mono`;

export interface ImportPasteFormProps {
  onImported: (prefill: ImportTextResponse) => void;
  onCancel: () => void;
}

function lignes(bloc: string): string[] {
  return bloc.split('\n').map((ligne) => ligne.trim()).filter((ligne) => ligne.length > 0);
}

/** Une différence entre les deux lectures, telle qu'elle se lit à l'écran. */
interface Difference {
  champ: string;
  avant: string;
  apres: string;
}

function resume(prefill: ImportTextResponse): { titre: string; ingredients: number; etapes: number; notes: number } {
  return {
    titre: prefill.title,
    ingredients: lignes(prefill.ingredients_text).length,
    etapes: lignes(prefill.steps_text).length,
    notes: prefill.notes === null ? 0 : lignes(prefill.notes).length,
  };
}

function compte(n: number, singulier: string, pluriel: string): string {
  return `${String(n)} ${n > 1 ? pluriel : singulier}`;
}

/**
 * Ce qui a changé entre les deux lectures (US-70 : « l'écran montre ce qui a
 * changé »). Volontairement grossier — un titre, trois compteurs — plutôt
 * qu'une comparaison ligne à ligne : ce qu'on veut savoir avant de valider,
 * c'est si l'affinage a *déplacé* quelque chose, pas la liste exhaustive des
 * mots qui diffèrent. Le détail se lit dans l'écran d'édition, juste après.
 */
function differences(local: ImportTextResponse, ia: ImportTextResponse): Difference[] {
  const a = resume(local);
  const b = resume(ia);
  const trouvees: Difference[] = [];

  if (a.titre !== b.titre) {
    trouvees.push({
      champ: 'Titre',
      avant: a.titre === '' ? 'aucun' : `« ${a.titre} »`,
      apres: b.titre === '' ? 'aucun' : `« ${b.titre} »`,
    });
  }
  if (a.ingredients !== b.ingredients) {
    trouvees.push({
      champ: 'Ingrédients',
      avant: compte(a.ingredients, 'ligne', 'lignes'),
      apres: compte(b.ingredients, 'ligne', 'lignes'),
    });
  }
  if (a.etapes !== b.etapes) {
    trouvees.push({
      champ: 'Étapes',
      avant: compte(a.etapes, 'étape', 'étapes'),
      apres: compte(b.etapes, 'étape', 'étapes'),
    });
  }
  if (a.notes !== b.notes) {
    trouvees.push({
      champ: 'Notes',
      avant: compte(a.notes, 'ligne', 'lignes'),
      apres: compte(b.notes, 'ligne', 'lignes'),
    });
  }
  return trouvees;
}

/** Le résumé d'une lecture, tel qu'il s'affiche avant de passer à l'édition. */
function Resume({ prefill }: { prefill: ImportTextResponse }): React.JSX.Element {
  const { titre, ingredients, etapes, notes } = resume(prefill);
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
      <dt className="text-label">Titre</dt>
      <dd>{titre === '' ? <span className="text-foreground-muted">aucun titre détecté</span> : titre}</dd>
      <dt className="text-label">Ingrédients</dt>
      <dd>{compte(ingredients, 'ligne', 'lignes')}</dd>
      <dt className="text-label">Étapes</dt>
      <dd>
        {etapes === 0
          // Une liste vide est un résultat, pas une panne (D6) : le procédé
          // d'une recette de vidéo est dans la vidéo, et personne ne l'a
          // inventé. Le dire ici évite de le découvrir sur une fiche muette.
          ? <span className="text-foreground-muted">aucune — ce texte ne décrit pas le procédé</span>
          : compte(etapes, 'étape', 'étapes')}
      </dd>
      <dt className="text-label">Notes</dt>
      <dd>{notes === 0 ? <span className="text-foreground-muted">aucune</span> : compte(notes, 'ligne', 'lignes')}</dd>
    </dl>
  );
}

/**
 * Collage de texte libre (US-03, étendu par US-26 puis US-70) : un des points
 * d'entrée de l'écran « Ajout / édition » (§6), avant `RecipeForm`.
 *
 * **Deux lectures, et le choix appartient à qui colle.** Le découpage local du
 * serveur reste le défaut : il est instantané, gratuit, et c'est le seul
 * chemin qui existe sans clé. Quand une clé est configurée, le résultat de ce
 * découpage s'affiche **d'abord**, avec un bouton secondaire « Affiner avec
 * l'IA » — l'IA s'exécute quand un geste l'a demandée (D3), jamais d'elle-même
 * et jamais en substitution silencieuse.
 *
 * **Sans clé, cet écran est exactement celui d'avant US-70** : pas d'étape
 * intermédiaire, pas de bouton mort. Ajouter un « voici ce qu'on a trouvé,
 * continuer ? » à qui n'a aucun autre choix serait un clic pour rien.
 */
export function ImportPasteForm({ onImported, onCancel }: ImportPasteFormProps): React.JSX.Element {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState<'local' | 'ia' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [local, setLocal] = useState<ImportTextResponse | null>(null);
  const [ia, setIa] = useState<ImportTextResponse | null>(null);
  const [affiche, setAffiche] = useState<'local' | 'ia'>('local');
  const [aiAvailable, setAiAvailable] = useState(false);

  /**
   * La **promesse** de disponibilité, et pas seulement son résultat.
   *
   * L'état suffit pour l'affichage, pas pour la décision : coller un texte et
   * cliquer aussitôt est le geste normal, et `GET /api/ia` peut n'avoir pas
   * encore répondu. Décider sur l'état, c'est décider sur `false` par défaut —
   * l'écran sauterait l'étape de choix précisément chez qui vient de poser sa
   * clé, sans que rien ne le signale. La promesse rend la décision
   * déterministe quel que soit l'ordre d'arrivée.
   */
  const dispoRef = useRef<Promise<boolean>>(Promise.resolve(false));

  // Même mécanisme que `App` (US-69) : la disponibilité se relit sur
  // `mlc:ia`, pour qu'une clé posée dans les réglages fasse apparaître le
  // bouton sans rechargement. Un échec laisse l'IA considérée absente — c'est
  // la dégradation attendue, pas une erreur à afficher.
  useEffect(() => {
    const controller = new AbortController();
    function relire(): void {
      const promesse = readAiAvailability(controller.signal)
        .then(({ available }) => available)
        .catch(() => false);
      dispoRef.current = promesse;
      void promesse.then((available) => { setAiAvailable(available); });
    }
    relire();
    window.addEventListener('mlc:ia', relire);
    return () => { controller.abort(); window.removeEventListener('mlc:ia', relire); };
  }, []);

  const courant = affiche === 'ia' && ia !== null ? ia : local;

  // Le texte a changé : les deux lectures portent sur un texte qui n'existe
  // plus. Les garder à l'écran laisserait valider un résultat périmé.
  function handleTextChange(valeur: string): void {
    setText(valeur);
    setLocal(null);
    setIa(null);
    setAffiche('local');
    setError(null);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = text.trim();
    if (trimmed.length === 0 || submitting !== null) return;

    setSubmitting('local');
    setError(null);
    Promise.all([importFromText({ text }), dispoRef.current])
      .then(([prefill, disponible]) => {
        // Sans clé, rien à proposer : on va droit à l'écran d'édition, comme
        // avant US-70.
        if (!disponible) { onImported(prefill); return; }
        setLocal(prefill);
        setIa(null);
        setAffiche('local');
      })
      .catch((err: unknown) => { setError(err instanceof Error ? err.message : 'Erreur inconnue.'); })
      .finally(() => { setSubmitting(null); });
  }

  function affiner(): void {
    if (submitting !== null) return;
    setSubmitting('ia');
    setError(null);
    importFromText({ text, avec_ia: true })
      .then((prefill) => { setIa(prefill); setAffiche('ia'); })
      // L'appel a échoué : le découpage local reste affiché, et le message
      // dit quoi faire. Rien ne se substitue à rien.
      .catch((err: unknown) => { setError(err instanceof Error ? err.message : 'Erreur inconnue.'); })
      .finally(() => { setSubmitting(null); });
  }

  const changements = local !== null && ia !== null ? differences(local, ia) : [];

  return (
    <form onSubmit={handleSubmit} aria-label="Coller le texte d’une recette" className="flex flex-col gap-4">
      {error !== null && <Banner tone="danger">{error}</Banner>}

      {/* Mise en contexte (audit UX/UI passe 2, I7), même principe qu'ImportUrlForm.tsx. */}
      <Card surface className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="mt-0.5 size-6 shrink-0 text-primary"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="6" y="4" width="12" height="16" rx="2" />
            <path d="M9 4V3.5A1.5 1.5 0 0 1 10.5 2h3A1.5 1.5 0 0 1 15 3.5V4M9 10h6M9 14h6M9 18h3" />
          </svg>
          <div className="flex flex-col gap-1">
            <h2 className="text-heading-2">Coller un texte</h2>
            <p className="text-sm text-foreground-secondary">
              Collez le texte reçu par message ou copié d’un site : nous repérons automatiquement les ingrédients et
              les étapes, en vrac ou non — rien n’est enregistré avant votre validation.
            </p>
          </div>
        </div>

        <label className="flex flex-col gap-1 text-sm">
          Texte de la recette
          <textarea
            className={textareaClass}
            value={text}
            onChange={(e) => { handleTextChange(e.target.value); }}
            rows={12}
            placeholder="Collez ici le texte reçu par message : ingrédients, étapes, tout en vrac."
          />
        </label>
      </Card>

      {courant !== null && (
        <Card surface className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h3 className="text-heading-3">
              {affiche === 'ia' ? 'Ce que l’IA a lu' : 'Ce que le découpage automatique a trouvé'}
            </h3>
            <p className="text-sm text-foreground-secondary">
              {affiche === 'ia'
                ? 'Relisez les quantités : un modèle propose une valeur là où les règles laissent un champ vide.'
                : 'Instantané et sans appel extérieur. Vous pouvez continuer ainsi, ou demander à l’IA de relire le texte.'}
            </p>
          </div>

          <Resume prefill={courant} />

          {affiche === 'ia' && (
            <div className="flex flex-col gap-1">
              <h4 className="text-label">Ce qui a changé</h4>
              {changements.length === 0 ? (
                <p className="text-sm text-foreground-secondary">
                  Rien : l’IA a lu la même chose que le découpage automatique.
                </p>
              ) : (
                <ul className="flex flex-col gap-1 text-sm">
                  {changements.map((changement) => (
                    <li key={changement.champ}>
                      <span className="text-label">{changement.champ}</span> — {changement.avant}{' '}
                      <Icon name="fleche-droite" size={0.9} /> <span className="sr-only">devient</span> {changement.apres}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Card>
      )}

      <div className="flex flex-wrap gap-3">
        {courant === null ? (
          <Button type="submit" variant="primary" disabled={text.trim().length === 0 || submitting !== null} aria-busy={submitting !== null}>
            Analyser le texte
          </Button>
        ) : (
          <Button
            type="button"
            variant="primary"
            onClick={() => { onImported(courant); }}
            disabled={submitting !== null}
          >
            Continuer
          </Button>
        )}

        {/* Visible seulement si une clé est configurée : pas de bouton mort
            (§1 « toute fonction IA se dégrade proprement »). */}
        {courant !== null && aiAvailable && ia === null && (
          <Button type="button" variant="secondary" onClick={affiner} disabled={submitting !== null} aria-busy={submitting === 'ia'}>
            Affiner avec l’IA
          </Button>
        )}

        {affiche === 'ia' && (
          <Button type="button" variant="quiet" onClick={() => { setAffiche('local'); }} disabled={submitting !== null}>
            Revenir au découpage automatique
          </Button>
        )}

        {affiche === 'local' && ia !== null && (
          <Button type="button" variant="quiet" onClick={() => { setAffiche('ia'); }} disabled={submitting !== null}>
            Revoir la lecture de l’IA
          </Button>
        )}

        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting !== null}>
          Annuler
        </Button>
      </div>
    </form>
  );
}
